import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);
    const shouldOpenImmediately = isMobileBreakpoint() && !productGrid;

    if (shouldOpenImmediately) {
      this.#showMobileLoadingState();
      this.#openQuickAddModal();
    }

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    if (!shouldOpenImmediately) {
      this.#openQuickAddModal();
    }
  };

  #showMobileLoadingState() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const loadingElement = document.createElement('div');
    loadingElement.className = 'quick-add-modal__loading';
    loadingElement.setAttribute('aria-label', 'Loading product');
    loadingElement.setAttribute('role', 'status');
    loadingElement.innerHTML = '<span class="quick-add-modal__loading-spinner" aria-hidden="true"></span>';

    modalContent.replaceChildren(loadingElement);
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const productCard = /** @type {HTMLElement | null} */ (this.closest('product-card'));
    const isProductPageCardQuickAdd = Boolean(
      isMobileBreakpoint() && productCard?.closest('main[data-template*="product"] .section-resource-list')
    );
    const isHotspotQuickAdd = Boolean(this.closest('product-hotspot-component')) || isProductPageCardQuickAdd;
    modalContent.classList.toggle('quick-add-modal__content--hotspot', isHotspotQuickAdd);

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const productMedia = productGrid.querySelector('.product-information__media');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const wishlistButton = productGrid.querySelector('.product-form-buttons__wishlist');
      const productPageUrl = this.productPageUrl;
      const productSeries = isHotspotQuickAdd
        ? this.#getHotspotSeriesLabel(productDetails) || this.#getHotspotSeriesLabel(productGrid)
        : '';
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = productPageUrl;

      if (productMedia instanceof HTMLElement && productPageUrl) {
        const productMediaLink = document.createElement('a');
        productMediaLink.href = productPageUrl;
        productMediaLink.classList.add('quick-add-modal__media-link');
        productMediaLink.setAttribute('aria-label', this.dataset.productTitle || 'View product details');
        productMedia.appendChild(productMediaLink);
      }

      if (isHotspotQuickAdd && productMedia instanceof HTMLElement && !productMedia.querySelector('.thumb-series')) {
        const thumbSeries = document.createElement('div');
        thumbSeries.className = 'thumb-series';
        thumbSeries.textContent = this.#formatHotspotThumbSeries(productSeries) || 'Burning Way';
        productMedia.appendChild(thumbSeries);
      }

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      if (productSeries) {
        const productSeriesElement = document.createElement('p');
        productSeriesElement.className = 'product-series';
        productSeriesElement.textContent = productSeries;
        productHeader.appendChild(productSeriesElement);
      }
      productHeader.appendChild(productTitle);
      if (wishlistButton && !isHotspotQuickAdd) {
        wishlistButton.classList.add('quick-add-modal__header-wishlist');
        productHeader.appendChild(wishlistButton);
      }
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      if (isHotspotQuickAdd) {
        try {
          this.#prepareHotspotMobileActions(productGrid);
        } catch (_error) {
          // Keep the quick-add modal usable even if a third-party wishlist node changes shape.
        }
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#connectWishlistButton(modalContent);
    this.#refreshWishlistHero(modalContent);
  }

  /**
   * @param {Element | null} source
   * @returns {string}
   */
  #getHotspotSeriesLabel(source) {
    if (!source) return '';

    const selectors = [
      '[class*="ai-product-collections-item"] a',
      '[class*="ai-product-collections-item"]',
      '[data-product-series]',
      '.product-series',
      'a[href*="/collections/"]',
    ];

    for (const selector of selectors) {
      const element = source.querySelector(selector);
      const text = element?.textContent?.replace(/\s+/g, ' ').trim();

      if (text) return text;
    }

    return '';
  }

  /**
   * @param {string} seriesLabel
   * @returns {string}
   */
  #formatHotspotThumbSeries(seriesLabel) {
    return seriesLabel.replace(/^the\s+/i, '').trim();
  }

  /**
   * Keeps hotspot mobile quick-add actions in the requested two-row layout:
   * Add to cart full width, then wishlist plus accelerated checkout.
   * @param {Element} productGrid
   */
  #prepareHotspotMobileActions(productGrid) {
    const productFormButtons = /** @type {HTMLElement | null} */ (productGrid.querySelector('.product-form-buttons'));
    if (!productFormButtons) return;

    productFormButtons.classList.add('product-form-buttons--hotspot-mobile-actions');

    const addToCartWrapper = Array.from(productFormButtons.children).find(
      (child) => child instanceof HTMLElement && child.querySelector('add-to-cart-component')
    );
    if (addToCartWrapper instanceof HTMLElement) {
      addToCartWrapper.classList.add('product-form-buttons__add-to-cart-action');
    }

    let wishlistButton = /** @type {HTMLElement | null} */ (
      productGrid.querySelector('.product-form-buttons__wishlist')
    );

    if (!wishlistButton) {
      wishlistButton = this.#createHotspotWishlistButton();
    }

    if (!wishlistButton) return;

    wishlistButton.classList.remove('quick-add-modal__header-wishlist');

    if (wishlistButton.parentElement !== productFormButtons) {
      productFormButtons.insertBefore(wishlistButton, addToCartWrapper || productFormButtons.firstElementChild);
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  #createHotspotWishlistButton() {
    const sourceCustomButton = /** @type {HTMLElement | null} */ (
      this.closest('product-hotspot-component')?.querySelector(
        '.hotspot-wishlist-proxy .wishlist-hero-custom-button[data-wlh-id]'
      )
    );
    if (!sourceCustomButton) return null;

    const wrapper = document.createElement('div');
    wrapper.className = 'product-form-buttons__wishlist';
    wrapper.innerHTML = `
      <span class="product-form-buttons__wishlist-placeholder" aria-hidden="true">
        <svg viewBox="0 0 512 512" focusable="false">
          <path fill="currentColor" d="M458.4 64.3C400.6 15.7 311.3 23 256 79.3 200.7 23 111.4 15.6 53.6 64.3-21.6 127.6-10.6 230.8 43 285.5l175.4 178.7c10 10.2 23.4 15.9 37.6 15.9 14.3 0 27.6-5.6 37.6-15.8L469 285.6c53.5-54.7 64.7-157.9-10.6-221.3zm-23.6 187.5L259.4 430.5c-2.4 2.4-4.4 2.4-6.8 0L77.2 251.8c-36.5-37.2-43.9-107.6 7.3-150.7 38.9-32.7 98.9-27.8 136.5 10.5l35 35.7 35-35.7c37.8-38.5 97.8-43.2 136.5-10.6 51.1 43.1 43.5 113.9 7.3 150.8z"></path>
        </svg>
      </span>
    `;

    wrapper.appendChild(sourceCustomButton.cloneNode(false));
    return wrapper;
  }

  /**
   * Some third-party wishlist apps only initialize buttons that exist on first page load.
   * Quick-add content is fetched later, so this proxies the modal heart to an already
   * initialized wishlist button from the product card/hotspot when needed.
   * @param {Element} modalContent
   */
  #connectWishlistButton(modalContent) {
    const targetWrapper = /** @type {HTMLElement | null} */ (
      modalContent.querySelector('.product-form-buttons__wishlist')
    );
    const targetCustomButton = /** @type {HTMLElement | null} */ (
      targetWrapper?.querySelector('.wishlist-hero-custom-button[data-wlh-id]')
    );

    if (!targetWrapper || !targetCustomButton || targetWrapper.dataset.wishlistProxyReady === 'true') return;

    targetWrapper.dataset.wishlistProxyReady = 'true';
    this.#syncWishlistButtonState(targetWrapper, targetCustomButton);

    targetWrapper.addEventListener(
      'click',
      (event) => {
        if (targetWrapper.dataset.forwardingWishlistClick === 'true') {
          window.setTimeout(() => this.#syncWishlistButtonState(targetWrapper, targetCustomButton), 250);
          return;
        }

        const clickTarget = event.target instanceof Element ? event.target : null;
        const initializedTargetButton = targetCustomButton.querySelector('button, a, [role="button"]');
        if (initializedTargetButton && clickTarget && initializedTargetButton.contains(clickTarget)) {
          window.setTimeout(() => this.#syncWishlistButtonState(targetWrapper, targetCustomButton), 250);
          return;
        }

        const sourceButton =
          (initializedTargetButton instanceof HTMLElement ? initializedTargetButton : null) ||
          this.#findInitializedWishlistButton(targetCustomButton);
        if (sourceButton) {
          event.preventDefault();
          event.stopPropagation();

          targetWrapper.dataset.forwardingWishlistClick = 'true';
          sourceButton.click();
          window.setTimeout(() => {
            delete targetWrapper.dataset.forwardingWishlistClick;
          }, 0);

          window.setTimeout(() => this.#syncWishlistButtonState(targetWrapper, targetCustomButton), 80);
          window.setTimeout(() => this.#syncWishlistButtonState(targetWrapper, targetCustomButton), 350);
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.#toggleFallbackWishlist(targetWrapper, targetCustomButton);
        this.#refreshWishlistHero(modalContent);
      },
      { capture: true }
    );
  }

  /**
   * @param {HTMLElement} targetCustomButton
   * @returns {HTMLElement | null}
   */
  #findInitializedWishlistButton(targetCustomButton) {
    const productId = targetCustomButton.dataset.wlhId;
    const variantId = targetCustomButton.dataset.wlhVariantid;
    const candidates = Array.from(document.querySelectorAll('.wishlist-hero-custom-button[data-wlh-id]')).filter(
      (element) =>
        element instanceof HTMLElement &&
        element !== targetCustomButton &&
        element.dataset.wlhId === productId &&
        (!variantId || element.dataset.wlhVariantid === variantId)
    );

    const preferredContainers = [
      this,
      this.closest('product-hotspot-component'),
      this.closest('product-card'),
      document,
    ];

    for (const container of preferredContainers) {
      if (!container) continue;
      const match = candidates.find((candidate) => container.contains(candidate));
      const button = match?.querySelector('button, a, [role="button"]');
      if (button instanceof HTMLElement) return button;
    }

    for (const candidate of candidates) {
      const button = candidate.querySelector('button, a, [role="button"]');
      if (button instanceof HTMLElement) return button;
    }

    return null;
  }

  /**
   * @param {HTMLElement} targetWrapper
   * @param {HTMLElement} sourceCustomButton
   */
  #syncWishlistButtonState(targetWrapper, sourceCustomButton) {
    const sourceButton =
      sourceCustomButton.querySelector('button, a, [role="button"]') ||
      this.#findInitializedWishlistButton(sourceCustomButton);
    const stateText = [
      sourceCustomButton.className,
      sourceCustomButton.getAttribute('aria-label'),
      sourceCustomButton.getAttribute('title'),
      sourceButton?.getAttribute('aria-label'),
      sourceButton?.getAttribute('title'),
      sourceButton?.className,
    ]
      .filter(Boolean)
      .join(' ');
    const fallbackActive = this.#isFallbackWishlisted(sourceCustomButton);
    const isActive = fallbackActive || /remove|added|in wishlist|selected|active/i.test(stateText);

    targetWrapper.classList.toggle('is-wishlist-active', isActive);
    targetWrapper.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  }

  /**
   * Local fallback used only when Wishlist Hero has not initialized dynamic quick-add content.
   * @param {HTMLElement} targetWrapper
   * @param {HTMLElement} customButton
   */
  #toggleFallbackWishlist(targetWrapper, customButton) {
    const key = customButton.dataset.wlhVariantid || customButton.dataset.wlhId;
    if (!key) return;

    const wishlist = this.#getFallbackWishlist();
    const isActive = Boolean(wishlist[key]);

    if (isActive) {
      delete wishlist[key];
    } else {
      wishlist[key] = {
        id: customButton.dataset.wlhId || '',
        variantId: customButton.dataset.wlhVariantid || '',
        link: customButton.dataset.wlhLink || '',
        price: customButton.dataset.wlhPrice || '',
        name: customButton.dataset.wlhName || '',
        image: customButton.dataset.wlhImage || '',
        addedAt: Date.now(),
      };
    }

    window.localStorage.setItem('theme:wishlist', JSON.stringify(wishlist));
    targetWrapper.classList.toggle('is-wishlist-active', !isActive);
    targetWrapper.setAttribute('aria-pressed', !isActive ? 'true' : 'false');
    document.dispatchEvent(
      new CustomEvent('theme:wishlist:change', {
        detail: {
          active: !isActive,
          item: wishlist[key] || {
            id: customButton.dataset.wlhId || '',
            variantId: customButton.dataset.wlhVariantid || '',
          },
        },
      })
    );
  }

  #getFallbackWishlist() {
    try {
      return JSON.parse(window.localStorage.getItem('theme:wishlist') || '{}') || {};
    } catch (_error) {
      return {};
    }
  }

  /**
   * @param {HTMLElement} customButton
   */
  #isFallbackWishlisted(customButton) {
    const key = customButton.dataset.wlhVariantid || customButton.dataset.wlhId;
    if (!key) return false;

    return Boolean(this.#getFallbackWishlist()[key]);
  }

  /**
   * Ask Wishlist Hero-style scripts to scan dynamic quick-add markup.
   * @param {Element} context
   */
  #refreshWishlistHero(context) {
    if (!context.querySelector('.wishlist-hero-custom-button[data-wlh-id]')) return;

    const events = [
      'wishlist-hero:refresh',
      'wishlist-hero:updated',
      'wishlistHero:refresh',
      'wishlistHero:updated',
      'wishlisthero:refresh',
      'wlh:refresh',
    ];

    events.forEach((eventName) => {
      document.dispatchEvent(new CustomEvent(eventName, { detail: { context } }));
      window.dispatchEvent(new CustomEvent(eventName, { detail: { context } }));
    });

    const candidates = [
      window.WishlistHero,
      window.WishListHero,
      window.wishlistHero,
      window.WishListHero_SDK,
      window.wishlistHeroSDK,
      window.wlh,
    ];
    const methods = ['init', 'initialize', 'render', 'refresh', 'reload', 'load', 'scan', 'process'];

    candidates.forEach((candidate) => {
      if (!candidate || typeof candidate !== 'object') return;
      methods.forEach((method) => {
        if (typeof candidate[method] !== 'function') return;

        try {
          candidate[method](context);
        } catch (error) {
          try {
            candidate[method]();
          } catch (_ignored) {
            // Ignore third-party refresh errors; the proxy click remains as fallback.
          }
        }
      });
    });
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
